import React, { useRef } from 'react';
import cn from 'classnames';
import { PapiAttachmentFile, PapiDeviation } from '@wix/da-papi-types';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import Slider, {
  DefaultNextButton,
  DefaultPreviousButton,
} from '@wix/da-ds/pkg/Slider';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import {
  BiData,
  BiContextProvidedValue,
  MultiImageFileClickBiEvent,
} from '@wix/da-bi/pkg/events';
import noop from '../../../utils/noop';
import { ImageSource } from '../../../DeviationViews/Full/types';
import { toMediaSrcFile } from '../../../utils/DeviationMediaService/helpers';
import {
  getDeviationResourceType,
  getDeviationTitle,
} from '../../../DeviationViews/_helpers';
import { AttachmentUsageType } from '../../../redux/uploads/types';

import s from './AdditionalMediaSlider.scss';

export const DEFAULT_THUMB_SIZE = 40;

interface Props {
  deviation: PapiDeviation;
  additionalMedia?: PapiAttachmentFile[];
  thumbSize?: number;
  disabled?: boolean;
  /** blur the thumbs on the clientside */
  shouldBlur?: boolean;
  className?: string;
  selected?: ImageSource;
  onSelect?: (attachment?: ImageSource) => void;
}

/** A Slider that allows selecting either one of a devation's
 *  additionalMedia or the deviation itself */
const AdditionalMediaSlider: React.FC<Props> = ({
  deviation,
  additionalMedia = [],
  thumbSize = DEFAULT_THUMB_SIZE,
  disabled,
  shouldBlur,
  className,
  selected,
  onSelect = noop,
}) => {
  const needsToScroll = useRef<boolean>(true);
  if (!additionalMedia.length) {
    return null;
  }

  // todo: deviation is never the same object from render to render. why?
  const isDeviationSelected =
    selected?.type === deviation.type &&
    selected?.deviationId === deviation.deviationId;

  // only scroll on the first render
  const selectedIndex = needsToScroll.current
    ? additionalMedia.findIndex(media => media === selected)
    : undefined;
  needsToScroll.current = false;

  // not using a dumbo here so it will match attachment thumbs
  const deviationMediaType = deviation.media?.types?.[0];
  if (!deviationMediaType) {
    return null;
  }

  const deviationThumbData = toMediaSrcFile(deviation, deviationMediaType);
  return (
    <Slider
      className={cn(s['root'], className)}
      slimSpacing
      scrollToIndex={selectedIndex}
      smoothInitialScroll={false}
      onScrollBiData={{
        typeid: getDeviationResourceType(deviation),
        itemid: deviation.deviationId,
        sectionname: BiContextProvidedValue,
      }}
      // todo: only using these button overrides to add an arrow className
      renderPrevButton={({ onClick, className: arrowClassName }) => (
        <DefaultPreviousButton
          className={cn(arrowClassName, s['slider-arrow'])}
          arrowSize={IconSize.SMALL}
          onClick={onClick}
        />
      )}
      renderNextButton={({ onClick, className: arrowClassName }) => (
        <DefaultNextButton
          className={cn(arrowClassName, s['slider-arrow'])}
          arrowSize={IconSize.SMALL}
          onClick={onClick}
        />
      )}
    >
      <PlainButtonOrLink
        key={'deviation'}
        style={{ width: thumbSize, height: thumbSize }}
        className={cn(s['thumb'], isDeviationSelected && s['selected-thumb'])}
        disabled={disabled}
        onClick={() => onSelect(deviation)}
        biData={BiData<MultiImageFileClickBiEvent>({
          evid: 310,
          deviationid: deviation.deviationId,
          rank: 0,
          usagetype: AttachmentUsageType.USAGETYPE_ORIGINAL_FILE,
        })}
      >
        {deviationThumbData && (
          <img
            className={cn(s['thumb-image'], shouldBlur && s['blurred'])}
            src={deviationThumbData.src}
            alt={getDeviationTitle(deviation)}
          />
        )}
      </PlainButtonOrLink>

      {additionalMedia.map((attachment, idx) => {
        const selectedMediaType = attachment.media?.types?.[0];
        const thumbData =
          !!selectedMediaType && toMediaSrcFile(attachment, selectedMediaType);
        if (!thumbData) {
          return null;
        }

        return (
          <PlainButtonOrLink
            key={`additionalMedia-${attachment.fileId}`}
            style={{ width: thumbSize, height: thumbSize }}
            className={cn(
              s['thumb'],
              selected === attachment && s['selected-thumb']
            )}
            disabled={disabled}
            onClick={() => onSelect(attachment)}
            biData={BiData<MultiImageFileClickBiEvent>({
              evid: 310,
              deviationid: deviation.deviationId,
              fileid: attachment.fileId || 0,
              rank: idx + 1,
              usagetype: AttachmentUsageType.USAGETYPE_ADDITIONAL_IMAGE,
            })}
          >
            <img
              className={cn(s['thumb-image'], shouldBlur && s['blurred'])}
              src={thumbData.src}
              alt={attachment.filename}
            />
          </PlainButtonOrLink>
        );
      })}
    </Slider>
  );
};
AdditionalMediaSlider.displayName = 'AdditionalMediaSlider';

export default AdditionalMediaSlider;
